import request from "./core/index";

// Types for analytics data
export interface AnalyticsData {
  totalVisitors: number;
  pageViews: number;
  workPageViews: Record<string, number>;
}

// 방문 통계 데이터 인터페이스
export interface VisitData {
  pagePath: string;
  referrer: string;
  workId?: number;
  userIdentifier: string;
  userAgent: string;
  ipAddress?: string; // 서버에서 처리될 수 있음
}

// 중복 호출 방지를 위한 최근 호출 기록
const recentCalls: Record<string, number> = {};
const DEBOUNCE_TIME = 5000; // 5초 내 중복 호출 방지

// 실제 진행 중인 호출을 추적
let pendingApiCall: Promise<void> | null = null;

// 사용자 식별자 생성 함수
const generateRandomId = (): string => {
  return Math.random().toString(36).substring(2, 15) + 
         Math.random().toString(36).substring(2, 15);
};

// 사용자 식별자 가져오기 (또는 생성)
const getUserIdentifier = (): string => {
  const storedId = localStorage.getItem('userIdentifier');
  
  if (!storedId) {
    const newId = generateRandomId();
    localStorage.setItem('userIdentifier', newId);
    return newId;
  }
  
  return storedId;
};

// 통합된 방문 통계 수집 함수
export const trackVisit = async (pagePath: string, workId?: number): Promise<void> => {
  // 중복 호출 방지 - 같은 경로와 workId에 대해 일정 시간 내 중복 호출 방지
  const callKey = `${pagePath}${workId ? `-${workId}` : ''}`;
  const now = Date.now();
  if (recentCalls[callKey] && now - recentCalls[callKey] < DEBOUNCE_TIME) {
    console.log(`[Analytics] Prevented duplicate call for ${callKey} (within ${DEBOUNCE_TIME}ms)`);
    return;
  }
  
  // 진행 중인 API 호출이 있다면 기다림
  if (pendingApiCall) {
    console.log('[Analytics] Waiting for pending API call to complete');
    await pendingApiCall;
  }
  
  // 이 호출을 최근 호출로 등록
  recentCalls[callKey] = now;
  
  try {
    const visitData: VisitData = {
      pagePath,
      referrer: document.referrer,
      userIdentifier: getUserIdentifier(),
      userAgent: navigator.userAgent,
    };

    // 작품 페이지인 경우 workId 추가 (유효한 숫자인 경우에만)
    if (workId !== undefined && workId > 0 && !isNaN(workId)) {
      visitData.workId = workId;
      console.log(`[Analytics] Adding workId to visit data: ${workId} for path: ${pagePath}`);
    } else if (workId !== undefined) {
      console.log(`[Analytics] Invalid workId detected: ${workId} for path: ${pagePath}, not adding to request`);
    }

    console.log(`[Analytics] Sending visit data:`, {
      ...visitData,
      userAgent: visitData.userAgent.substring(0, 50) + '...' // userAgent는 길어서 일부만 표시
    });
    
    // 새로운 API 호출을 생성하고 저장
    pendingApiCall = request.post('/api/v1/statistics/visit', visitData)
      .then(() => {
        console.log(`[Analytics] Visit tracked successfully for: ${pagePath}`);
      })
      .catch((error) => {
        console.error('[Analytics] Failed to track visit:', error);
      })
      .finally(() => {
        pendingApiCall = null; // 호출 완료 후 null로 설정
      });
    
    await pendingApiCall;
  } catch (error) {
    console.error('[Analytics] Error in trackVisit:', error);
    pendingApiCall = null;
  }
};

// 아래 함수들은 이제 사용하지 않지만 호환성을 위해 유지
export const trackVisitor = async (): Promise<void> => {
  console.log('[Analytics] trackVisitor() is deprecated, use trackVisit() instead');
  const currentPath = window.location.pathname;
  await trackVisit(currentPath);
};

export const trackPageView = async (pagePath: string): Promise<void> => {
  console.log('[Analytics] trackPageView() is deprecated, use trackVisit() instead');
  await trackVisit(pagePath);
};

export const trackWorkPageView = async (workId: string): Promise<void> => {
  console.log('[Analytics] trackWorkPageView() is deprecated, use trackVisit() instead');
  const currentPath = window.location.pathname;
  const numericWorkId = parseInt(workId, 10);
  if (!isNaN(numericWorkId)) {
    await trackVisit(currentPath, numericWorkId);
  } else {
    console.error('[Analytics] Invalid workId:', workId);
    await trackVisit(currentPath);
  }
};

// 관리자 대시보드에서 사용할 통계 데이터 가져오기 함수
export const getAnalyticsData = async (): Promise<AnalyticsData> => {
  return {
    totalVisitors: 0,
    pageViews: 0,
    workPageViews: {}
  };
}; 